import { AfterViewInit, Component, forwardRef, Input, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Environment, EnvironmentService } from '@galaxy/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { HttpClient } from '@angular/common/http';

interface Editor {
  getContent: () => string;
  setContent: (content: string) => string;
  insertContent: (content: string) => void;
  on: () => void;
}

declare let tinymce: any;

const HTML_EDITOR_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => HtmlEditorComponent),
  multi: true,
};

@Component({
  imports: [NgIf, NgClass, AsyncPipe],
  selector: 'automata-html-editor',
  template: `
    <div *ngIf="{ editorReady: editorReady$ | async } as data">
      <div class="stencil-shimmer" [ngClass]="{ hidden: data.editorReady }"></div>
      <textarea [ngClass]="{ hidden: !data.editorReady }" id="{{ elementId }}"></textarea>
    </div>
  `,
  styles: [
    `
      .hidden {
        visibility: hidden;
        max-height: 0;
      }
      .stencil-shimmer {
        width: 100%;
        height: 5em;
      }
    `,
  ],
  providers: [HTML_EDITOR_ACCESSOR],
})
export class HtmlEditorComponent implements AfterViewInit, ControlValueAccessor, OnDestroy {
  @Input() elementId: string;
  @Input() menubar: any = false;
  @Input() menu: any = false;
  @Input() toolbar: any = false;
  @Input() plugins: string;
  @Input() width: number;
  @Input() height: number;
  @Input() setup: any;
  // This is for uploading images
  @Input() showUploadTab = false;
  @Input() maxImageSize = 5000000;
  @Input() invalidElements: string;
  @Input() disabled: boolean;

  editorReady$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  editorReady$ = this.editorReady$$.asObservable();
  private initialValue: string;
  private editor: Editor;
  private onChange: any;
  private onTouched: any;
  private httpHost: string;
  private grpcHost: string;
  private fileUploadUrl = `${this.mediaHTTPHost}/image/upload`;
  private fileCreateUrl = `${this.mediaGRPCHost}/media.v1.ImageService/Create`;
  private fileGetUrl = `${this.mediaHTTPHost}/image/get`;
  private tinyMCEFileUploadSuccessCallback: any;
  private tinyMCEFileUploadFailureCallback: any;
  private supportedFileTypes: string[] = ['image/jpeg', 'image/png', 'image/gif'];

  private subscriptions: Subscription[] = [];

  constructor(
    private readonly translateService: TranslateService,
    private http: HttpClient,
    private environmentService: EnvironmentService,
  ) {}

  ngAfterViewInit(): void {
    window.setTimeout(() => {
      tinymce.baseURL = 'https://storage.googleapis.com/developers-vendasta-client/assetsv2';
      tinymce.init({
        readonly: this.disabled,
        selector: '#' + this.elementId,
        width: this.width,
        height: this.height,
        menubar: this.menubar,
        menu: this.menu,
        toolbar: this.toolbar,
        plugins: this.plugins,
        convert_urls: false,
        allow_conditional_comments: true,
        valid_children: '+body[style]',
        invalid_elements: this.invalidElements || null,
        branding: false,
        setup: (editor) => {
          this.editor = editor;

          editor.on('init', () => {
            if (this.initialValue) {
              this.editor.setContent(this.initialValue);
            }
          });

          // prevents this.editor from being undefined on events
          const updateHandler = () => {
            this.onEditorUpdate();
          };
          editor.on('keyup', updateHandler);
          editor.on('change', updateHandler);
          editor.on('NodeChange', updateHandler);

          if (this.setup) {
            this.setup(editor);
          }
        },
        images_upload_handler: (blobInfo: any, success: any, failure: any): void => {
          // Check if image is a valid type and size
          let validFileType = false;
          for (const supportedFileType of this.supportedFileTypes) {
            if (blobInfo.blob().type === supportedFileType) {
              validFileType = true;
            }
          }
          if (!validFileType) {
            failure('Filetype must be jpeg, gif or png');
            return;
          }
          if (blobInfo.blob().size > this.maxImageSize) {
            failure('Image must be less than 5 MB');
            return;
          }
          // set success and failure variables to proper context
          this.tinyMCEFileUploadSuccessCallback = success;
          this.tinyMCEFileUploadFailureCallback = failure;
          // start uploading image
          this.uploadImage(blobInfo);
        },
        //  We have to pass any non-empty string to show the uploader tab in tinymce image uploader.
        //  And since we are using images_upload_handler, this url is not being used at all.
        images_upload_url: this.showUploadTab ? 'any-non-empty-value' : '',
        init_instance_callback: () => {
          this.editorReady$$.next(true);
        },
      });
    }, 0);
  }

  onEditorUpdate(): void {
    if (!this.editor) {
      return;
    }
    const content = this.editor.getContent();
    if (this.onChange) {
      this.onChange(content);
    }
  }

  writeValue(value: any): void {
    if (value !== undefined) {
      // need to cache this so it can set when editor is instantiated
      this.initialValue = value;
    }
  }

  insertAtCursor(text: string): void {
    this.editor.insertContent(text);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private uploadImage(blobInfo: any): void {
    const fileUploadBody = new FormData();
    fileUploadBody.append('image', blobInfo.blob(), blobInfo.filename());
    this.subscriptions.push(
      this.http.post(this.fileUploadUrl, fileUploadBody, { withCredentials: true }).subscribe(
        (result: any) => {
          this.callToCreateImage(result.url);
        },
        () => {
          this.tinyMCEFileUploadFailureCallback('Failed to upload. Please try again');
        },
      ),
    );
  }

  private callToCreateImage(fileUrl: string): void {
    const fileCreateBody = {
      servingUrl: fileUrl,
    };
    this.subscriptions.push(
      this.http.post(this.fileCreateUrl, fileCreateBody, { withCredentials: true }).subscribe(
        (result: any) => {
          // Added the "=s0" to the end of the URL to ensure the img tag gets the image with the original uploaded resolution
          this.tinyMCEFileUploadSuccessCallback(this.fileGetUrl + '/' + result.imageId + '=s0');
        },
        () => {
          this.tinyMCEFileUploadFailureCallback('Failed to upload. Please try again');
        },
      ),
    );
  }

  private get mediaHTTPHost(): string {
    if (this.httpHost) {
      return this.httpHost;
    }
    switch (this.environmentService.getEnvironment()) {
      case Environment.LOCAL:
        this.httpHost = 'https://media-test.apigateway.co';
        break;
      case Environment.TEST:
        this.httpHost = 'https://media-test.apigateway.co';
        break;
      case Environment.DEMO:
        this.httpHost = 'https://media-demo.apigateway.co';
        break;
      case Environment.PROD:
        this.httpHost = 'https://media-prod.apigateway.co';
        break;
    }
    return this.httpHost;
  }

  private get mediaGRPCHost(): string {
    if (this.grpcHost) {
      return this.grpcHost;
    }
    switch (this.environmentService.getEnvironment()) {
      case Environment.LOCAL:
        this.grpcHost = 'https://media-api-test.apigateway.co';
        break;
      case Environment.TEST:
        this.grpcHost = 'https://media-api-test.apigateway.co';
        break;
      case Environment.DEMO:
        this.grpcHost = 'https://media-api-demo.apigateway.co';
        break;
      case Environment.PROD:
        this.grpcHost = 'https://media-api-prod.apigateway.co';
        break;
    }
    return this.grpcHost;
  }

  ngOnDestroy(): void {
    tinymce.remove(this.editor);
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
